import { useState, useCallback, useEffect, useRef } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { Link } from '@tanstack/react-router';
import { DocsLayout } from '../../components/docs/DocsLayout.tsx';
import { useDocumentTitle } from '../../hooks/useDocumentTitle.ts';
import { useT } from '../../i18n.ts';
import { getApiBaseUrl } from '../../config.ts';
import { media } from '@goalpost/content';

// One tab per assistant in the connection guide. The id doubles as the URL
// hash that opens the page on that tab (`/docs/ai-integration#chatgpt`), so
// it is part of the page's public surface — keep ids stable.
const assistants = [
  { id: 'claude', label: 'Claude' },
  { id: 'claude-code', label: 'Claude Code' },
  { id: 'chatgpt', label: 'ChatGPT' },
  { id: 'codex-cli', label: 'Codex CLI' },
  { id: 'cursor', label: 'Cursor' },
  { id: 'other', label: 'Other assistants' },
] as const;

type AssistantId = (typeof assistants)[number]['id'];

const isAssistantId = (value: string): value is AssistantId =>
  assistants.some((assistant) => assistant.id === value);

export function DocsAiIntegration() {
  const t = useT();
  useDocumentTitle(t('docs.ai-integration.title'));

  const apiBase = getApiBaseUrl();
  const mcpUrl = apiBase ? `${apiBase}/api/mcp` : `${window.location.origin}/api/mcp`;
  const pluginRepo = 'movegoalpost/goalpost-plugin';
  const mcpConfigJson = JSON.stringify(
    {
      mcpServers: {
        goalpost: {
          type: 'streamable-http',
          url: mcpUrl,
        },
      },
    },
    null,
    2,
  );

  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = useCallback(async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      prompt('Copy to clipboard:', text);
    }
  }, []);

  // A hand-taken screenshot of an assistant's settings UI, published by the
  // `connection-guide` copy target (marketing/scripts/targets.ts) and resolved
  // from the media manifest by its filename. Like MediaImage it renders nothing
  // until the asset has been pushed, so the guide reads cleanly without it.
  const screenshot = (file: string, alt: string) => {
    const asset = media(file);
    if (!asset) return null;
    return (
      <figure className="mkt-doc-figure">
        <img src={asset.url} alt={alt} loading="lazy" decoding="async" />
      </figure>
    );
  };

  const copyButton = (id: string, value: string) => (
    <button
      type="button"
      className="mkt-doc-copy"
      onClick={() => handleCopy(id, value)}
    >
      {copiedId === id ? 'Copied!' : 'Copy'}
    </button>
  );

  // Which assistant's instructions are showing. A matching URL hash opens
  // the page on that tab, and picking a tab writes its id back to the hash so
  // the choice survives a reload and can be linked to. The hash is read after
  // mount rather than in the initializer so the first render has no window
  // dependency.
  const [assistant, setAssistant] = useState<AssistantId>(assistants[0].id);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const readHash = () => {
      const hash = window.location.hash.slice(1);
      if (isAssistantId(hash)) setAssistant(hash);
    };
    readHash();
    window.addEventListener('hashchange', readHash);
    return () => window.removeEventListener('hashchange', readHash);
  }, []);

  const selectAssistant = (id: AssistantId, focus: boolean) => {
    setAssistant(id);
    // Keep the entry's existing state: the router stores its own
    // bookkeeping there, and replacing it with null breaks back/forward.
    history.replaceState(history.state, '', `#${id}`);
    if (focus) tabRefs.current[assistants.findIndex((a) => a.id === id)]?.focus();
  };

  // Arrow keys move between tabs and select as they go, the standard
  // tablist pattern; Home and End jump to the ends.
  const handleTabKeyDown = (event: KeyboardEvent, index: number) => {
    const last = assistants.length - 1;
    let next: number;
    switch (event.key) {
      case 'ArrowRight':
        next = index === last ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        next = index === 0 ? last : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }
    event.preventDefault();
    selectAssistant(assistants[next].id, true);
  };

  const panel = (id: AssistantId, children: ComponentChildren) => (
    <div
      id={`assistant-panel-${id}`}
      role="tabpanel"
      aria-labelledby={`assistant-tab-${id}`}
      tabIndex={0}
      hidden={assistant !== id}
      className="mkt-doc-tabpanel"
      data-gp-name="assistant-panel"
      data-assistant={id}
    >
      {children}
    </div>
  );

  return (
    <DocsLayout
      path="/docs/ai-integration"
      title={t('docs.ai-integration.label')}
      lede="Connect an AI assistant to Goalpost so it can read your documents and draft changes for you to review. The assistant works with your access and can’t approve anything; approvals stay human."
    >
      <h2 id="overview">How it works</h2>
      <p>
        Goalpost connects to AI assistants through a standard called MCP, which
        most modern assistants support (Claude, ChatGPT, Cursor, and others).
        Once connected, the assistant can read your documents and draft{' '}
        <Link to="/docs/change-management">changes</Link> against them, and you
        review and submit those drafts yourself. Until Goalpost is listed in
        your assistant’s directory, you add it by address, which takes a
        minute.
      </p>
      <p>
        The first time you use a Goalpost tool from an assistant, you’ll be
        asked to sign in and pick a workspace to authorize. Each connection is
        pinned to one workspace; if you work across several, add a separate
        connection for each.
      </p>
      <h2 id="server-url">Your connection address</h2>
      <p>
        Wherever an assistant asks for a Goalpost or “MCP server” address, use
        this:
      </p>
      <div className="mkt-doc-codeblock">
        <code className="mkt-doc-codeblock-content">{mcpUrl}</code>
        {copyButton('url', mcpUrl)}
      </div>

      <h2 id="connect">Connect your assistant</h2>
      <p>
        Setup differs slightly per assistant, but the address above is always
        what you provide. Pick yours.
      </p>

      <div
        role="tablist"
        aria-label="Assistant"
        className="mkt-doc-tabs"
        data-gp-name="assistant-tabs"
      >
        {assistants.map(({ id, label }, index) => (
          <button
            key={id}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            id={`assistant-tab-${id}`}
            role="tab"
            aria-selected={assistant === id}
            aria-controls={`assistant-panel-${id}`}
            tabIndex={assistant === id ? 0 : -1}
            className="mkt-doc-tab"
            data-gp-name="tab"
            data-assistant={id}
            onClick={() => selectAssistant(id, false)}
            onKeyDown={(event) => handleTabKeyDown(event, index)}
          >
            {label}
          </button>
        ))}
      </div>

      {panel(
        'claude',
        <>
          <p>
            Claude Desktop and claude.ai share one setup: Goalpost is added as
            a custom connector. In Claude Desktop the steps start from{' '}
            <strong>Settings</strong>; on claude.ai they start from{' '}
            <strong>Customize</strong>.
          </p>
          <ol>
            <li>
              Open <strong>Connectors</strong>.
            </li>
            <li>
              Click <strong>+ Add</strong> and choose{' '}
              <strong>Add custom connector</strong>.
              {screenshot('Claude_add_connector.webp', 'Claude Desktop Connectors settings with the Add menu open, showing Add custom connector')}
            </li>
            <li>
              Enter <strong>Goalpost</strong> as the name, paste your
              connection address, and click <strong>Continue</strong>:
              <div className="mkt-doc-codeblock">
                <code className="mkt-doc-codeblock-content">{mcpUrl}</code>
                {copyButton('claude', mcpUrl)}
              </div>
              {screenshot('Claude_custom_connector.webp', 'Claude Desktop Add custom connector dialog filled in with the name Goalpost and the Goalpost connection address')}
            </li>
            <li>
              Claude detects Goalpost’s sign-in setup. Keep{' '}
              <strong>Sign in now</strong> and{' '}
              <strong>Register automatically</strong> selected and connect, then
              sign in to Goalpost and pick the workspace to authorize.
              {screenshot('Claude_connector_signin.webp', 'Claude Desktop connector dialog for Goalpost with Sign in now and Register automatically detected and selected')}
            </li>
          </ol>
        </>,
      )}

      {panel(
        'claude-code',
        <>
          <p>From a Claude Code document, run:</p>
          <div className="mkt-doc-codeblock">
            <pre className="mkt-doc-codeblock-content">{`claude mcp add goalpost --transport streamable-http ${mcpUrl}`}</pre>
            {copyButton(
              'claude-code',
              `claude mcp add goalpost --transport streamable-http ${mcpUrl}`,
            )}
          </div>
          <p>
            Claude Code walks you through sign-in automatically the first time
            you use a Goalpost tool.
          </p>
        </>,
      )}

      {panel(
        'chatgpt',
        <>
          <p>
            ChatGPT connects to Goalpost as a custom MCP server. On the web:
          </p>
          <ol>
            <li>
              Open <strong>Plugins</strong> in the sidebar, click{' '}
              <strong>Add</strong>, and choose{' '}
              <strong>Add custom MCP server</strong>.
              {screenshot('chatgpt_mcp_add.webp', 'ChatGPT web Plugins page with the Add menu open, showing Create plugin, Upload plugin archive, and Add custom MCP server')}
            </li>
            <li>
              Enter <strong>Goalpost</strong> as the name, keep the connection
              on <strong>Server URL</strong>, paste your connection address,
              and leave authentication on <strong>OAuth</strong>:
              <div className="mkt-doc-codeblock">
                <code className="mkt-doc-codeblock-content">{mcpUrl}</code>
                {copyButton('chatgpt', mcpUrl)}
              </div>
            </li>
            <li>
              Acknowledge the custom-server notice and click{' '}
              <strong>Create as a plugin</strong>. ChatGPT prompts you to sign
              in the first time you use a Goalpost tool.
              {screenshot('chatgpt_mcp_config.webp', 'ChatGPT Add custom MCP server dialog with the name Goalpost, the Goalpost connection address, OAuth authentication, and a Create as a plugin button')}
            </li>
          </ol>
          <p>
            In the ChatGPT desktop app, open <strong>Settings</strong>, then{' '}
            <strong>Plugins</strong>, switch to the <strong>MCPs</strong> tab,
            click <strong>Add</strong>, and choose{' '}
            <strong>Add MCP server</strong>. Name it Goalpost, set the type to{' '}
            <strong>Streamable HTTP</strong>, paste the same address as the
            URL, leave the token and header fields empty, and click{' '}
            <strong>Save</strong>.
          </p>
          {screenshot('chatgpt_desktop_mcp.webp', 'ChatGPT desktop Connect to a custom MCP form with the name Goalpost, type Streamable HTTP, and the Goalpost connection address as the URL')}
        </>,
      )}

      {panel(
        'codex-cli',
        <>
          <p>
            Codex is OpenAI’s terminal agent. The Goalpost plugin bundles the
            connection for it; add its marketplace from a shell:
          </p>
          <div className="mkt-doc-codeblock">
            <pre className="mkt-doc-codeblock-content">{`codex plugin marketplace add ${pluginRepo}`}</pre>
            {copyButton('codex-cli', `codex plugin marketplace add ${pluginRepo}`)}
          </div>
          <p>
            Then, inside a Codex session, run <code>/plugins</code> and install{' '}
            <strong>Goalpost</strong>. You’ll be asked to sign in the first time
            you use a Goalpost tool.
          </p>
        </>,
      )}

      {panel(
        'cursor',
        <>
          <p>
            Open Cursor’s Settings → MCP and add a new server with this
            configuration:
          </p>
          <div className="mkt-doc-codeblock">
            <pre className="mkt-doc-codeblock-content">{mcpConfigJson}</pre>
            {copyButton('cursor', mcpConfigJson)}
          </div>
          <p>
            Cursor opens a browser window for sign-in the first time you use a
            Goalpost tool.
          </p>
        </>,
      )}

      {panel(
        'other',
        <>
          <p>
            Any MCP-compatible assistant can connect. Point it at your
            connection address using the streamable-HTTP transport and OAuth
            sign-in; compliant assistants handle the rest automatically. Where
            the assistant takes a JSON server entry, this one works:
          </p>
          <div className="mkt-doc-codeblock">
            <pre className="mkt-doc-codeblock-content">{mcpConfigJson}</pre>
            {copyButton('other', mcpConfigJson)}
          </div>
        </>,
      )}

      <h2 id="what-can">What it can do</h2>
      <ul>
        <li>List the documents in your workspace and read their details.</li>
        <li>Read recent changes, including drafts you have in progress.</li>
        <li>
          Create new draft changes (adding, removing, moving, or replacing
          details) for you to review, and submit a draft for approval.
        </li>
        <li>
          Update the description or content of a draft you own, and split,
          merge, or copy drafts.
        </li>
        <li>Create and edit labels.</li>
        <li>
          If you own the workspace: create and manage teams, their members,
          and which documents they can access, and create and manage approval
          rules.
        </li>
      </ul>
    </DocsLayout>
  );
}
